import type { PayloadAction } from '@reduxjs/toolkit';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { DeleteProducerPayload, GetProducerListResponse } from '../store/types/ProducerSliceState';
import { CreateProducerEntityRequest } from '../store/types/requests/CreateProducerEntityRequest';
import type {
  LinkProducerPayload,
  LinkProducerToClaimRequest,
  SearchExistingProducersApiResponse,
} from './types/linkProducerResponse';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getClaimSetClaimListData } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import { MIN_SEARCH_LENGTH } from '../store/constants/searchLength';
import { createProducer, resetCreateProducerState, setCreateProducerLoadingStatus } from '../store/createProducerSlice';
import {
  getLinkProducerExistingUserById,
  linkProducer,
  resetLinkProducerState,
  setLinkProducerStatus,
  setUsersListForProducerLink,
} from '../store/linkProducerSlice';
import {
  decrementProducerGroupListPage,
  decrementProducerListPage,
  deleteProducer,
  getProducerGroupListSuccess,
  getProducerListDataError,
  getProducerListSuccess,
  getProducerLists,
  incrementProducerGroupListPage,
  incrementProducerListPage,
  searchProducerLists,
  setLoadingProducerListData,
} from '../store/producersSlice';

function* linkProducerSaga({ payload }: PayloadAction<LinkProducerPayload>) {
  try {
    const { claimId, producerId, producerGroupId } = payload;
    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    const body: LinkProducerToClaimRequest = {
      claimId,
      producerId: producerId ?? null,
      producerGroupId: producerGroupId ?? null,
    };

    yield call(HttpService.post, '/examiner/producer/link-to-claim', body);

    SnacksService.add({
      type: SnackType.Success,
      title: 'Producer linked successfully!',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    yield put(getClaimSetClaimListData({ claimSetId: String(currentClaimSetId) }));
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to link producer!',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } finally {
    yield put(setLinkProducerStatus(false));
    yield put(resetLinkProducerState());
    ModalService.close(Modals.LinkProducer);
  }
}

function* fetchExistingProducersSaga({
  payload,
}: PayloadAction<{ searchExpression: string; producerGroupId?: number | null }>) {
  try {
    const query = new URLSearchParams({
      searchExpression: payload.searchExpression,
      ...(payload.producerGroupId ? { producerGroupId: String(payload.producerGroupId) } : {}),
    }).toString();

    const { data }: HttpServiceResponse<SearchExistingProducersApiResponse> = yield call(
      HttpService.get,
      `/examiner/search/search-existing-producers?${query}`,
    );

    const usersList = [
      ...data.producers.map((agent) => ({
        id: agent.id,
        entityType: EdsEntityCodes.Producer,
        name:
          agent.firstName || agent.lastName
            ? `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim()
            : agent.producerNumber,
        producerNumber: agent.producerNumber,
      })),
      ...data.producerGroups.map((agency) => ({
        id: agency.id,
        entityType: EdsEntityCodes.ProducerGroup,
        name: agency.name,
        producerNumber: agency.producerNumber,
      })),
    ];

    yield put(setUsersListForProducerLink(usersList as any));
  } catch (error) {
    console.error(error);
  }
}

function* searchProducerListsSaga() {
  yield put(setLoadingProducerListData(true));

  const searchQuery: string = yield select((state: RootState) => state.producers.searchQuery);
  const producerListPagination = yield select((state: RootState) => state.producers.producerList.pagination);
  const producerGroupListPagination = yield select((state: RootState) => state.producers.produceGroupList.pagination);

  try {
    const producersUrl = `search?searchType=${EdsEntityCodes.Producer}&${QueryParamsService.constructSearchParamsFromRecord(
      {
        searchValue: searchQuery,
        page: producerListPagination.page,
        perPage: producerListPagination.perPage,
      },
    )}`;

    const producerGroupsUrl = `search?searchType=${EdsEntityCodes.ProducerGroup}&${QueryParamsService.constructSearchParamsFromRecord(
      {
        searchValue: searchQuery,
        page: producerGroupListPagination.page,
        perPage: producerGroupListPagination.perPage,
      },
    )}`;

    const [producerListData, producerGroupListData]: [
      HttpServiceResponse<GetProducerListResponse>,
      HttpServiceResponse<GetProducerListResponse>,
    ] = yield all([
      call(HttpService.get, producersUrl, {}, { baseURL: import.meta.env.VITE_API_URL_EDS }),
      call(HttpService.get, producerGroupsUrl, {}, { baseURL: import.meta.env.VITE_API_URL_EDS }),
    ]);

    const normalizedProducerList = yield call(EdsAdapterService.normalize, producerListData.data.listings);
    const normalizedProducerGroupList = yield call(EdsAdapterService.normalize, producerGroupListData.data.listings);

    EdsDebugService.logResponse(EdsEntityCodes.Producer, {
      rawResponse: producerListData.data,
      normalizedResponse: normalizedProducerList,
    });

    EdsDebugService.logResponse(EdsEntityCodes.ProducerGroup, {
      rawResponse: producerGroupListData.data,
      normalizedResponse: normalizedProducerGroupList,
    });

    yield put(
      getProducerListSuccess({
        shouldRewrite: true,
        data: { ...producerListData.data, listings: normalizedProducerList },
      }),
    );

    yield put(
      getProducerGroupListSuccess({
        shouldRewrite: true,
        data: { ...producerGroupListData.data, listings: normalizedProducerGroupList },
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(getProducerListDataError());
  } finally {
    yield put(setLoadingProducerListData(false));
  }
}

function* getProducerListsSaga({ type }) {
  yield put(setLoadingProducerListData(true));

  const producerListPagination = yield select((state: RootState) => state.producers.producerList.pagination);
  const producerGroupListPagination = yield select((state: RootState) => state.producers.produceGroupList.pagination);
  const sorting = yield select((state: RootState) => state.producers.savedSortingForSending);
  const filters = yield select((state: RootState) => state.producers.savedFiltersForSending);

  try {
    const producerUrl = `/filter/${EdsEntityCodes.Producer}?${QueryParamsService.constructSearchParamsFromRecord({
      page: producerListPagination.page,
      perPage: producerListPagination.perPage,
      sortBy: sorting?.propertyName,
      sortDirection: sorting?.sortDirection,
    })}`;

    const groupUrl = `/filter/${EdsEntityCodes.ProducerGroup}?${QueryParamsService.constructSearchParamsFromRecord({
      page: producerGroupListPagination.page,
      perPage: producerGroupListPagination.perPage,
      sortBy: sorting?.propertyName,
      sortDirection: sorting?.sortDirection,
    })}`;

    const [producerListData, producerGroupListData]: [
      HttpServiceResponse<GetProducerListResponse>,
      HttpServiceResponse<GetProducerListResponse>,
    ] = yield all([
      call(HttpService.post, producerUrl, filters || [], { baseURL: import.meta.env.VITE_API_URL_EDS }),
      call(HttpService.post, groupUrl, filters || [], { baseURL: import.meta.env.VITE_API_URL_EDS }),
    ]);

    const normalizedProducers = yield call(EdsAdapterService.normalize, producerListData.data.listings);
    const normalizedGroups = yield call(EdsAdapterService.normalize, producerGroupListData.data.listings);

    yield put(
      getProducerListSuccess({
        shouldRewrite: type !== incrementProducerListPage.type,
        data: { ...producerListData.data, listings: normalizedProducers },
      }),
    );

    yield put(
      getProducerGroupListSuccess({
        shouldRewrite: type !== incrementProducerGroupListPage.type,
        data: { ...producerGroupListData.data, listings: normalizedGroups },
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(getProducerListDataError());
  } finally {
    yield put(setLoadingProducerListData(false));
  }
}

function* handleProducerPaginationSaga(action: PayloadAction<any>) {
  const searchQuery: string = yield select((state: RootState) => state.producers.searchQuery);

  if (searchQuery.trim()) {
    yield* searchProducerListsSaga();
  } else {
    yield* getProducerListsSaga(action);
  }
}

function* deleteProducerSaga({ payload }: PayloadAction<DeleteProducerPayload>) {
  try {
    if (!payload.id || !payload.entityType) return;

    const url =
      payload.entityType === EdsEntityCodes.Producer
        ? `/examiner/producer/producer/${payload.id}`
        : `/examiner/producer/producer-group/${payload.id}`;

    yield call(HttpService.delete, url);

    yield* searchProducerListsSaga();

    SnacksService.add({
      type: SnackType.Success,
      title: 'Producer was deleted successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to delete Producer, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  }
}

function* createProducerSaga({ payload }: PayloadAction<CreateProducerEntityRequest>) {
  try {
    yield put(setCreateProducerLoadingStatus(true));

    const searchQuery: string = yield select((state: RootState) => state.producers.searchQuery);
    const isSearchMode = searchQuery.trim().length >= MIN_SEARCH_LENGTH;

    yield call(HttpService.post, '/examiner/producer/create-producer', payload);

    if (!isSearchMode) {
      yield put(getProducerLists());
    }

    SnacksService.add({
      type: SnackType.Success,
      title: 'Producer was successfully created.',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to delete Producer, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } finally {
    ModalService.close(Modals.AddProducer);
    yield put(setCreateProducerLoadingStatus(false));
    yield put(resetCreateProducerState());
  }
}

export function* producersWatcher() {
  yield takeLatest(getLinkProducerExistingUserById.type, fetchExistingProducersSaga);
  yield takeLatest(linkProducer.type, linkProducerSaga);
  yield takeLatest(deleteProducer.type, deleteProducerSaga);
  yield takeLatest(createProducer.type, createProducerSaga);
  yield takeLatest(getProducerLists.type, getProducerListsSaga);
  yield takeLatest(searchProducerLists.type, searchProducerListsSaga);
  yield takeLatest(
    [
      incrementProducerListPage.type,
      decrementProducerListPage.type,
      incrementProducerGroupListPage.type,
      decrementProducerGroupListPage.type,
    ],
    handleProducerPaginationSaga,
  );
}
